import { Component, HostBinding, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { SvgIconComponent } from 'angular-svg-icon';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../../service/layout.service';
import { ExtendedMenuItem, BoxSection } from '../menu/menu.types';

@Component({
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, RippleModule, SvgIconComponent],
    templateUrl: './app.menuitem.html',
    animations: [
        trigger('children', [
            state(
                'collapsed',
                style({
                    height: '0'
                })
            ),
            state(
                'expanded',
                style({
                    height: '*'
                })
            ),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitem implements OnInit, OnDestroy {
    @Input() public item!: MenuItem;
    @Input() public index!: number;
    @Input() @HostBinding('class.layout-root-menuitem') public root!: boolean;
    @HostBinding('class.has-box-submenu') public get hasBoxSubmenuClass(): boolean {
        return this.isBoxSubmenu;
    }
    @Input() public parentKey!: string;
    @Input() public boxSections?: BoxSection[];
    @HostBinding('class.active-menuitem') public get activeClass(): boolean {
        return this.active && !this.root;
    }
    public active = false;
    /** Box-submenu only: index of the one expanded section, or `null` if all collapsed. */
    public expandedBoxSectionIndex: number | null = null;
    /** Box-submenu only: expanded item indices per expanded box section. */
    private readonly _expandedBoxItemIndexBySection = new Map<number, number | null>();
    public key: string = '';
    private menuSourceSubscription!: Subscription;
    private menuResetSubscription!: Subscription;
    public readonly router = inject(Router);
    private readonly _layoutService = inject(LayoutService);

    public get isDirectItem(): boolean {
        return !!this.item.routerLink && !this.item.items;
    }

    public get isParentItem(): boolean {
        return !this.isDirectItem;
    }

    public get isChildItem(): boolean {
        return !this.root;
    }
    public get submenuAnimation(): string {
        // All items (including root) respect active state for accordion behavior
        return this.active ? 'expanded' : 'collapsed';
    }

    public get isBoxSubmenu(): boolean {
        return (this.item as ExtendedMenuItem).menuType === 'box-submenu' && !!this.boxSections;
    }

    private isTasksBoxSubmenu(): boolean {
        return this.isBoxSubmenu && String(this.item.label ?? '').toLowerCase().startsWith('tasks');
    }

    private getMenuItemBadgeCount(item: MenuItem): number {
        const badge = item.badge;
        if (badge == null || badge === '') return 0;
        const parsed = Number(badge);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private boxSectionHasOpenTasks(section: BoxSection): boolean {
        if (this.getMenuItemBadgeCount({ badge: section.badge } as MenuItem) > 0) return true;
        return (section.items ?? []).some((item) => this.getMenuItemBadgeCount(item) > 0);
    }

    /** Expand the first Tasks section that has open tasks (no navigation). */
    private expandFirstOpenTasksSection(): void {
        if (!this.boxSections?.length) return;
        const openIdx = this.boxSections.findIndex((section) => this.boxSectionHasOpenTasks(section));
        if (openIdx >= 0) {
            this.expandedBoxSectionIndex = openIdx;
            return;
        }
        this.syncExpandedBoxSectionFromRoute();
    }

    public isBoxSectionActive(boxSection: BoxSection): boolean {
        if (!boxSection.items) return false;
        return boxSection.items.some((item) => this.isMenuItemActive(item));
    }

    private getMenuItemRouterUrl(item: MenuItem): string | null {
        if (!item.routerLink) {
            return null;
        }
        if (Array.isArray(item.routerLink)) {
            return item.routerLink.map((segment) => String(segment)).join('/');
        }
        return String(item.routerLink);
    }

    private isMenuItemActive(item: MenuItem): boolean {
        const url = this.getMenuItemRouterUrl(item);
        if (url) {
            const isActive = this.router.isActive(url, {
                paths: 'exact',
                queryParams: 'subset',
                matrixParams: 'ignored',
                fragment: 'ignored',
            });
            if (isActive) return true;
        }
        if (item.items?.length) {
            return item.items.some((child) => this.isMenuItemActive(child));
        }
        return false;
    }

    public isBoxSectionExpanded(sectionIndex: number): boolean {
        return this.expandedBoxSectionIndex === sectionIndex;
    }

    public toggleBoxSection(sectionIndex: number, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.expandedBoxSectionIndex === sectionIndex) {
            this.expandedBoxSectionIndex = null;
        } else {
            this.expandedBoxSectionIndex = sectionIndex;
        }
    }

    public isBoxItemExpanded(itemIndex: number): boolean {
        if (this.expandedBoxSectionIndex === null) return false;
        return (this._expandedBoxItemIndexBySection.get(this.expandedBoxSectionIndex) ?? null) === itemIndex;
    }

    public toggleBoxItem(itemIndex: number, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.expandedBoxSectionIndex === null) return;
        const sectionIndex = this.expandedBoxSectionIndex;
        const current = this._expandedBoxItemIndexBySection.get(sectionIndex) ?? null;
        this._expandedBoxItemIndexBySection.set(sectionIndex, current === itemIndex ? null : itemIndex);
    }

    /** Open the section that matches the current route, or the first section. */
    private syncExpandedBoxSectionFromRoute(): void {
        if (!this.boxSections?.length) return;
        const activeIdx = this.boxSections.findIndex((s) => this.isBoxSectionActive(s));
        const defaultCollapsed = (this.item as ExtendedMenuItem).defaultCollapsed === true;
        this.expandedBoxSectionIndex = activeIdx >= 0 ? activeIdx : (defaultCollapsed ? null : 0);
        this.syncExpandedBoxItemsFromRoute();
    }

    /** Expand Closing Opportunities (etc.) when a child route is active. */
    private syncExpandedBoxItemsFromRoute(): void {
        if (this.expandedBoxSectionIndex === null || !this.boxSections) return;
        const sectionIndex = this.expandedBoxSectionIndex;
        const section = this.boxSections[sectionIndex];
        section.items?.forEach((boxItem, itemIndex) => {
            if (!this.isMenuItemActive(boxItem)) return;
            this._expandedBoxItemIndexBySection.set(sectionIndex, itemIndex);
        });
    }

    public hasActiveChild(): boolean {
        if (this.item.items) {
            return this.item.items.some((child) => {
                if (child.routerLink) {
                    return this.router.isActive(child.routerLink[0], {
                        paths: 'exact',
                        queryParams: 'subset',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    });
                }
                return false;
            });
        }

        if (this.isBoxSubmenu && this.boxSections) {
            return this.boxSections.some((section) => this.isBoxSectionActive(section));
        }

        return false;
    }

    /**
     * Check if a child item is a box-submenu
     */
    public isBoxSubmenuForChild(child: MenuItem): boolean {
        const extendedItem = child as ExtendedMenuItem;
        return extendedItem.menuType === 'box-submenu' && !!extendedItem.boxSections;
    }

    /**
     * Get box sections for a child item
     */
    public getBoxSectionsForChild(child: MenuItem): BoxSection[] | undefined {
        const extendedItem = child as ExtendedMenuItem;
        return extendedItem.boxSections;
    }

    /**
     * Get icon path for menu item (SVG path or fallback to PrimeNG icon)
     */
    public getIconPath(): string | undefined {
        const extendedItem = this.item as ExtendedMenuItem;
        if (extendedItem.iconPath) {
            return extendedItem.iconPath;
        }
        // If no iconPath, return undefined (will use PrimeNG icon class)
        return undefined;
    }

    public ngOnInit(): void {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        if (this.item.routerLink) {
            this.updateActiveStateFromRoute();
        }

        this.syncSubmenuExpandedFromRoute();

        this.menuSourceSubscription = this._layoutService.menuSource$.subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
                    if (this.active && this.isBoxSubmenu) {
                        this.syncExpandedBoxSectionFromRoute();
                    }
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
                if (!this.active && this.isBoxSubmenu) {
                    this.expandedBoxSectionIndex = null;
                }
            });
        });

        this.menuResetSubscription = this._layoutService.resetSource$.subscribe(() => {
            this.active = false;
            this.expandedBoxSectionIndex = null;
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            if (this.item.routerLink) {
                this.updateActiveStateFromRoute();
            }
            this.syncSubmenuExpandedFromRoute();
        });
    }

    /** Expand Opportunities (and similar) when a child deals route is active. */
    private syncSubmenuExpandedFromRoute(): void {
        if ((this.item.items?.length || this.isBoxSubmenu) && this.hasActiveChild()) {
            this.active = true;
            if (this.isBoxSubmenu) {
                this.syncExpandedBoxSectionFromRoute();
            }
            this._layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    public itemClick(event: Event): void {
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // For box-submenu or items with submenus, prevent default navigation
        if (this.item.items || this.isBoxSubmenu) {
            event.preventDefault();
        }

        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // For box-submenu, treat it like a regular submenu (collapsible)
        if (this.item.items || this.isBoxSubmenu) {
            const wasActive = this.active;
            this.active = !this.active;
            if (this.isBoxSubmenu && this.active && !wasActive) {
                if (this.isTasksBoxSubmenu()) {
                    this.expandFirstOpenTasksSection();
                } else {
                    this.syncExpandedBoxSectionFromRoute();
                }
            }
        }

        this._layoutService.onMenuStateChange({ key: this.key });
    }

    private updateActiveStateFromRoute(): void {
        const activeRoute = this.router.isActive(this.item.routerLink[0], { paths: 'exact', queryParams: 'subset', matrixParams: 'ignored', fragment: 'ignored' });

        if (activeRoute) {
            this._layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    public ngOnDestroy(): void {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
